import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  MoreHorizontal,
  Eye,
  RefreshCw,
  Receipt,
  AlertCircle,
  DollarSign,
  CreditCard,
  Banknote,
  Wallet,
  Send,
  QrCode,
  Plus,
} from "lucide-react";
import { formatCurrency } from "@/lib/format";
import { useGetPayments } from "@/api/payments";
import { ProcessPaymentDialog } from "./process-payment-dialog";
import { RecordManualPaymentDialog } from "./record-manual-payment-dialog";
import { useQueryClient } from "@tanstack/react-query";

interface Payment {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  method: string;
  amountCents: number;
  transactionType?: "DEBIT" | "CREDIT";
  parentPaymentId?: string;
  stripePaymentIntentId?: string;
  stripeChargeId?: string;
  stripeRefundId?: string;
  failureReason?: string;
  processedBy?: string;
  order?: {
    id: string;
    orderNumber: number;
    placerCustomer?: {
      user?: {
        firstName: string;
        lastName: string;
        email: string;
      };
    };
  };
}

export function PaymentsTable() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);

  const {
    data: payments,
    isLoading,
    refetch,
  } = useGetPayments({
    limit: 100,
  });

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      PAID: "bg-green-100 text-green-800",
      PENDING: "bg-yellow-100 text-yellow-800",
      PROCESSING: "bg-blue-100 text-blue-800",
      FAILED: "bg-red-100 text-red-800",
      REFUNDED: "bg-purple-100 text-purple-800",
      PARTIAL_REFUND: "bg-purple-100 text-purple-800",
      VOIDED: "bg-gray-100 text-gray-800",
      PARTIAL: "bg-orange-100 text-orange-800",
    };

    return <Badge className={colors[status] || ""}>{status}</Badge>;
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case "SAVED_CARD":
        return <CreditCard className="h-4 w-4" />;
      case "CASH":
        return <Banknote className="h-4 w-4" />;
      case "E_TRANSFER":
        return <Send className="h-4 w-4" />;
      case "INVOICE":
        return <Receipt className="h-4 w-4" />;
      case "CREDIT":
        return <Wallet className="h-4 w-4" />;
      case "WECHAT":
        return <QrCode className="h-4 w-4" />;
      default:
        return <DollarSign className="h-4 w-4" />;
    }
  };

  const handlePaymentUpdate = () => {
    void refetch();
    void queryClient.invalidateQueries({ queryKey: ["payments"] });
    setProcessDialogOpen(false);
    setRecordDialogOpen(false);
  };

  // Filter payments based on search query
  const filteredPayments = payments?.filter((payment: Payment) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const orderNumber = payment.order?.orderNumber?.toString() || "";
    const customerName = payment.order?.placerCustomer?.user
      ? `${payment.order.placerCustomer.user.firstName} ${payment.order.placerCustomer.user.lastName}`.toLowerCase()
      : "";
    const email =
      payment.order?.placerCustomer?.user?.email?.toLowerCase() || "";
    const status = payment.status.toLowerCase();
    const method = payment.method.toLowerCase();

    return (
      orderNumber.includes(query) ||
      customerName.includes(query) ||
      email.includes(query) ||
      status.includes(query) ||
      method.includes(query)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search payments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 w-[300px]"
            />
          </div>
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
        <Button onClick={() => setRecordDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Record Payment
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center">
                    <div className="flex items-center justify-center">
                      <Skeleton className="h-8 w-[200px]" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredPayments && filteredPayments.length > 0 ? (
                filteredPayments.map((payment: Payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>
                      <div className="font-medium">
                        {payment.order?.orderNumber
                          ? `ORD-${payment.order.orderNumber}`
                          : "-"}
                      </div>
                    </TableCell>
                    <TableCell>
                      {payment.order?.placerCustomer?.user ? (
                        <div>
                          <div className="font-medium">
                            {payment.order.placerCustomer.user.firstName}{" "}
                            {payment.order.placerCustomer.user.lastName}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {payment.order.placerCustomer.user.email}
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getMethodIcon(payment.method)}
                        <span>{payment.method.replace(/_/g, " ")}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div
                        className={`font-medium ${payment.transactionType === "CREDIT" ? "text-red-600" : ""}`}
                      >
                        {payment.transactionType === "CREDIT" ? "-" : ""}
                        {formatCurrency(payment.amountCents)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {payment.transactionType === "CREDIT" ? (
                        <Badge className="bg-red-100 text-red-800">
                          CREDIT
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-800">
                          DEBIT
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(payment.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedPayment(payment);
                              setDetailsDialogOpen(true);
                            }}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {payment.status === "PENDING" && (
                            <>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedPayment(payment);
                                  setProcessDialogOpen(true);
                                }}
                              >
                                <CreditCard className="mr-2 h-4 w-4" />
                                Process Payment
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedPayment(payment);
                                  setRecordDialogOpen(true);
                                }}
                              >
                                <DollarSign className="mr-2 h-4 w-4" />
                                Record Manual Payment
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <AlertCircle className="h-8 w-8 text-muted-foreground" />
                      <p className="text-muted-foreground">No payments found</p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Payment Details Dialog */}
      {selectedPayment && (
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Payment Details</DialogTitle>
              <DialogDescription>
                Payment ID: {selectedPayment.id}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Order</p>
                  <p className="font-medium">
                    {selectedPayment.order?.orderNumber
                      ? `ORD-${selectedPayment.order.orderNumber}`
                      : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  {selectedPayment.transactionType === "CREDIT" ? (
                    <Badge className="bg-red-100 text-red-800">CREDIT</Badge>
                  ) : (
                    <Badge className="bg-blue-100 text-blue-800">DEBIT</Badge>
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  {getStatusBadge(selectedPayment.status)}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Method</p>
                  <div className="flex items-center gap-2">
                    {getMethodIcon(selectedPayment.method)}
                    <span>{selectedPayment.method.replace(/_/g, " ")}</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Amount</p>
                  <p className="font-medium">
                    {formatCurrency(selectedPayment.amountCents)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="font-medium">
                    {new Date(selectedPayment.createdAt).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Updated</p>
                  <p className="font-medium">
                    {new Date(selectedPayment.updatedAt).toLocaleString()}
                  </p>
                </div>
              </div>

              {selectedPayment.stripePaymentIntentId && (
                <div>
                  <p className="text-sm text-muted-foreground">
                    Stripe Payment Intent
                  </p>
                  <code className="text-sm bg-muted px-2 py-1 rounded">
                    {selectedPayment.stripePaymentIntentId}
                  </code>
                </div>
              )}

              {selectedPayment.stripeChargeId && (
                <div>
                  <p className="text-sm text-muted-foreground">Stripe Charge</p>
                  <code className="text-sm bg-muted px-2 py-1 rounded">
                    {selectedPayment.stripeChargeId}
                  </code>
                </div>
              )}

              {selectedPayment.stripeRefundId && (
                <div>
                  <p className="text-sm text-muted-foreground">Stripe Refund</p>
                  <code className="text-sm bg-muted px-2 py-1 rounded">
                    {selectedPayment.stripeRefundId}
                  </code>
                </div>
              )}

              {selectedPayment.failureReason && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <p className="font-medium">Failure Reason</p>
                    <p>{selectedPayment.failureReason}</p>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Process Payment Dialog */}
      {selectedPayment?.order && (
        <ProcessPaymentDialog
          open={processDialogOpen}
          onOpenChange={setProcessDialogOpen}
          order={{
            id: selectedPayment.order.id,
            orderNumber: selectedPayment.order.orderNumber,
            balanceCents: selectedPayment.amountCents,
          }}
          onSuccess={handlePaymentUpdate}
        />
      )}

      {/* Record Manual Payment Dialog */}
      {selectedPayment?.order && (
        <RecordManualPaymentDialog
          open={recordDialogOpen}
          onOpenChange={setRecordDialogOpen}
          order={{
            id: selectedPayment.order.id,
            orderNumber: selectedPayment.order.orderNumber,
            balanceCents: selectedPayment.amountCents,
          }}
          onSuccess={handlePaymentUpdate}
        />
      )}
    </div>
  );
}
